import type { BillingStatus, CompanyDto } from '@crv/contracts/tenancy';

/**
 * Manual QA checklist (web only):
 * - A: billingStatus=active => employees/time tracking accessible.
 * - B: billingStatus=trial with future expiry => accessible; banner shows days remaining.
 * - C: billingStatus=trial with past expiry => modules blocked; banner notes plan inactive.
 * - D: billingStatus=trial with null/invalid expiry => modules blocked (fail closed).
 * - E: no company selected => selection required before modules load.
 */

export type PlanStatusSource = Pick<CompanyDto, 'billingStatus' | 'trialExpiresAt'> | null | undefined;

const MS_IN_DAY = 24 * 60 * 60 * 1000;

export function isTrial(source: PlanStatusSource): boolean {
  return source?.billingStatus === 'trial';
}

export function isActive(source: PlanStatusSource): boolean {
  return source?.billingStatus === 'active';
}

export function isPastDue(source: PlanStatusSource): boolean {
  return source?.billingStatus === 'past_due';
}

export function isCanceled(source: PlanStatusSource): boolean {
  return source?.billingStatus === 'canceled';
}

export function isTrialExpired(source: PlanStatusSource, now = new Date()): boolean {
  const trialExpiresAt = source?.trialExpiresAt;
  if (!trialExpiresAt) return false;

  const expiresAt = Date.parse(trialExpiresAt);
  if (Number.isNaN(expiresAt)) {
    // Fail closed if the date is malformed.
    return true;
  }

  return now.getTime() > expiresAt;
}

/**
 * Returns whether the plan should be treated as usable for gated features.
 * If billingStatus is "trial" but the expiry is missing or invalid, this fails closed to avoid accidental access.
 */
export function isPlanUsable(source: PlanStatusSource, now = new Date()): boolean {
  const billingStatus: BillingStatus | null = source?.billingStatus ?? null;
  const trialExpiresAt = source?.trialExpiresAt;

  if (billingStatus === 'active') {
    return true;
  }

  if (billingStatus === 'trial') {
    if (!trialExpiresAt) {
      return false;
    }

    const expiresAt = Date.parse(trialExpiresAt);
    if (Number.isNaN(expiresAt)) {
      return false;
    }

    return now.getTime() <= expiresAt;
  }

  return false;
}

export function getTrialDaysRemaining(source: PlanStatusSource, now = new Date()): number | null {
  const trialExpiresAt = source?.trialExpiresAt;
  if (!trialExpiresAt) return null;

  const expiresAt = Date.parse(trialExpiresAt);
  if (Number.isNaN(expiresAt)) {
    return null;
  }

  const msRemaining = expiresAt - now.getTime();
  return Math.max(0, Math.ceil(msRemaining / MS_IN_DAY));
}
